import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useCallback, useEffect, useState } from 'react';
import { PublicNewsItem } from '../../../backend/models';
import { getSinglePublicNews } from '../../../backend/queries/public-news';
import { useLightbox } from '../../../components/hooks/useLightbox';
import { SectionLayout } from '../../../components/layouts/SectionLayout';
import { isInPricingPlan } from '../../../utils/flags';
import { getFileUrl } from '../../../utils/imaging';
import { handleAndGetTenantAsync, PageBaseProps } from '../../../utils/tenants';
import { htmlToText } from '../../../utils/textTools';
import { getCanonicalURL } from '../../../utils/seo/getCanonicalURL';
import { ShouldBeRedirected } from '..';
import { resolveObjectFit } from '../../../utils/objectFit';
import { IconBack } from '../../../components/icons';
import { useAppContext } from '../../../frontend/AppContext';
import { useRouter } from 'next/router';
import { getRelativeTime } from '../../../utils/time';
import { dateWithNoTime, dateWithTime } from '../../../utils/dt-helpers';
import { sanitizeTenant } from '../../../utils/sanitizeTenant';

type Props = PageBaseProps & {
    item: PublicNewsItem;
};

export default function NewPage(props: Props) {
    const LightBox = useLightbox();

    const [isNotFound, setIsNotFound] = useState(false);

    useEffect(() => {
        if (props?.item?.timeZone == "local" && props?.item?.publishedFrom != undefined) {
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const nowWithoutTime = dateWithNoTime();
            const nowWithTime = dateWithTime();

            const shouldDisplay = props.item.timeFrom !== undefined
                ? (props.item.publishedFrom ? dateWithTime(props?.item?.publishedFrom, props?.item?.timeFrom, props?.item?.timeZone ?? userTimezone) <= nowWithTime : true)
                : (props.item.publishedFrom ? dateWithNoTime(props?.item?.publishedFrom) <= nowWithoutTime : true);

            if (!shouldDisplay) {
                setIsNotFound(true);
            }
        }
    }, [props.item]);

    const articleContentRef = useCallback((node: HTMLDivElement) => {
        if (node) {
            try {
                const allImages = Array.from(node.querySelectorAll('img'));

                allImages.forEach(imgEl => {
                    imgEl.dataset.lghtb = 'true';
                });
            } catch (e) { }
        }
    }, [props.item.id]);


    if (isNotFound || !props.item) {
        return (
            <SectionLayout>
                <div className='mx-auto mt-10 max-w-3xl px-4 text-center'>
                    <h1 className='text-4xl font-bold'>404 - Page Not Found</h1>
                    <p className='mt-5'>The news article you are looking for was not found.</p>
                </div>
            </SectionLayout>
        );
    }

    return <>
        <Head>
            <title>{`${props.tenant.texts.announcementsMenuLinkText} - ${props.tenant.title}`}</title>
            <link rel='icon' type='image/png' href={getFileUrl(props.tenant.icons.favIconFileId)} />
            {props.canonical?.usingCustomDomain && <link rel='canonical' href={props.canonical?.canonicalUrl} />}
            <meta property="og:image" content={getFileUrl(props.tenant.icons.logoFileId)}></meta>
        </Head>
        <SectionLayout>
            <div className='mx-auto mt-10 max-w-3xl px-4'>
                <div className='mt-10 flex flex-col space-y-10' ref={articleContentRef}>
                    <PublicNewsItemComponent key={props.item.id as unknown as number} item={props.item} locale={props.tenant.currentLocale} />
                </div>
            </div>
            {LightBox}
        </SectionLayout>
    </>;
}

function PublicNewsItemComponent(props: { item: PublicNewsItem, locale: string; }) {
    const tenantCtx = useAppContext();

    const title = props.item.title;
    const content = props.item.content;

    const imageId = props.item?.imageId;
    const imagePos = props.item?.imagePos;
    const side = props.item?.side;

    const hasImage = imageId !== undefined && imageId !== null && imageId.length > 0 && side === "side";

    const style = hasImage ? {
        backgroundColor: 'transparent',
        backgroundImage: `url(https://cdn-assets.productfruits.com/${imageId})`,
        backgroundSize: 'var(--image-object-fit, cover)',
        backgroundPosition: 'var(--image-object-position, center)',
        backgroundRepeat: 'no-repeat',
        width: '100%',
        height: 'var(--side-height, 200px)',
        '--side-background-image-url': `url(https://cdn-assets.productfruits.com/${imageId})`,
        '--image-object-fit': resolveObjectFit(imagePos),
    } as React.CSSProperties : undefined;

    const router = useRouter();

    const curLng = tenantCtx?.tenant?.currentLocale || tenantCtx?.tenant?.supportedLanguages[0] || 'en';

    const handleBackToNews = useCallback(
        () => {
            router.push(`/${curLng}/news`);
        },
        [curLng, router?.push],
    );

    return <div className='news-item-v3 gap-5'>
        <div onClick={handleBackToNews} className='flex cursor-pointer flex-row flex-none gap-1 px-2 py-1.5 text-lg items-center'>
            <div className='px-0.5 items-center just flex'>
                <IconBack size='regular' />
            </div>

            <div className='px-1 font-semibold'>
                {tenantCtx?.tenant?.texts.backBtn ?? "Back"}
            </div>
        </div>

        <time className='text-[12px] text-gray-500'>{getRelativeTime(props.item.publishedAt, props.locale)}</time>
        <h2 className='font-extrabold text-4xl mb-5 mt-1'>{htmlToText(title)}</h2>

        {
            hasImage && <div style={style} className='flex w-full rounded-lg overflow-hidden'></div>
        }

        {content && <article className='article ck-content' dangerouslySetInnerHTML={{ __html: content }}></article>}
    </div >;
}

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
    const { tenant, auth, notFound } = await handleAndGetTenantAsync(ctx, true);

    if (notFound) {
        return {
            notFound: true
        };
    }

    if (!auth.isAuthorized || !tenant.announcements?.enabled || !isInPricingPlan('ann_v2npv', tenant.accountInfo)) {
        return {
            notFound: true
        };
    }

    const result = ShouldBeRedirected(ctx, tenant);
    if (result) return result;

    const articleId = BigInt(ctx.params?.slug?.toString().split('-')[0] ?? '');
    const newsItem = await getSinglePublicNews(articleId, tenant);

    if (!newsItem) {
        return {
            notFound: true
        };
    }

    const nowWithoutTime = dateWithNoTime();
    const nowWithTime = dateWithTime(undefined, undefined, undefined);

    const publishedFrom = newsItem?.publishedFrom !== null ? newsItem?.publishedFrom : undefined;
    const timeFrom = newsItem?.timeFrom !== null ? newsItem?.timeFrom : undefined;
    const timeZone = newsItem?.timeZone !== null ? newsItem?.timeZone : undefined;

    let shouldDisplay = false;

    if (newsItem?.timeZone != "local") {
        if (timeFrom !== undefined) {
            shouldDisplay = publishedFrom
                ? dateWithTime(publishedFrom, timeFrom, timeZone) <= nowWithTime
                : true;
        } else {
            shouldDisplay = publishedFrom
                ? dateWithNoTime(publishedFrom) <= nowWithoutTime
                : true;
        }
    } else {
        shouldDisplay = true;
    }

    if (!shouldDisplay) {
        return {
            notFound: true
        };
    }

    const canonical = getCanonicalURL(tenant, ctx.resolvedUrl);

    return {
        props: {
            tenant: sanitizeTenant(tenant),
            item: newsItem,
            auth,
            canonical
        }
    };
};